/* global google */

import createLatLngBounds from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/LatLngBounds';
import ResultsProvider from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/ResultsProvider';
import makeCreateFacilityMarker from '@PROJECT_ROOT/assets/desktop/js/search/mapsearch/FacilityMarker';

/**
 * Pretraga smještaja po karti
 *
 * Koristi Google Maps JavaScript API.
 *
 * @param {object} map Google mapa
 * @param {object} elements
 * {
 *     {object} mapSearchContainer HTML element,
 * }
 * @param {object} urls
 * {
 *     {string} results,
 *     {string} facilityIcon,
 *     {string} activeFacilityIcon,
 *     {string} facilitiesClusterIcon,
 *     {string} activeFacilitiesClusterIcon,
 * }
 * @param {function} analytics
 * @param {function} $ - jQuery
 * @param {object} options
 * {
 *     {function} fetchFilterData očekuje format kakav vraća .serializeArray() jquery metoda,
 *     {function} facilityMarkerClick,
 *     {function} onFacilityMarkerFocus,
 *     {function} getInitialViewport,
 * }
 * @constructor
 */
function MapSearch(map, elements, urls, analytics, $, options) {
    var self = this;

    validateType('object', elements);
    validateType('object', urls);
    validateType('function', $);

    var allowedBounds = new google.maps.LatLngBounds( // hrvatske granice
        new google.maps.LatLng(42.38, 13.49),
        new google.maps.LatLng(46.55, 19.45)
    );
    var lastValidCenter;
    var markers = [];
    var resultsProvider = new ResultsProvider(urls.results, $);
    var isUserInteractingWithMap = false;
    var previousZoom = map.getZoom();
    var readyCallback = function () {};
    var afterUpdateCallbacks = [];
    var beforeUpdateCallbacks = [];
    var locationMarker;
    var createMarker = makeCreateFacilityMarker({
        google: google,
        googleMap: map,
        facilityIcon: urls.facilityIcon,
        activeFacilityIcon: urls.activeFacilityIcon,
        facilityPriceSmallIcon: urls.facilityPriceSmallIcon,
        activeFacilityPriceSmallIcon: urls.activeFacilityPriceSmallIcon,
        facilityPriceMediumIcon: urls.facilityPriceMediumIcon,
        activeFacilityPriceMediumIcon: urls.activeFacilityPriceMediumIcon,
    });

    onReady(function () {
        readyCallback();
    });

    onUserInteractionEnd(function () {
        updateIfAllowedAndForbidForMiliseconds(500, {
            shouldFitAllResults: false,
            useLocationFilter: false,
        });
    });

    onCenterChanged(function () {
        if (!isCenterInsideAllowedBounds()) {
            panToLastValidCenter();

            return;
        }

        lastValidCenter = map.getCenter();
    });

    onZoomIn(function(current) {
        if (isUserInteractingWithMap) {
            analytics('RP-Mapa', 'Zoom', 'in', current);
        }
    });

    onZoomOut(function (current) {
        if (isUserInteractingWithMap) {
            analytics('RP-Mapa', 'Zoom', 'out', current);
        }
    });

    onDragEnd(function() {
        analytics('RP-Mapa', 'Drag');
    });

    onMapContainerClick(function () {
        isUserInteractingWithMap = true;
    });

    onControlKeyPressed(function () {
        isUserInteractingWithMap = true;
    });

    onMapContainerMouseout(function () {
        isUserInteractingWithMap = false;
    });

    /**
     * @param {object} results
     * @param {bool} shouldFitAllResults
     *
     * @see ResultsProvider - za format rezultata
     */
    self.setResults = function setResults(results, shouldFitAllResults) {
        refreshResults(results);

        if (shouldFitAllResults) {
            repositionMapToFitAllResults();
        }
    };

    /**
     * @param {object} location
     * {
     *     latitude: {number},
     *     longitude: {number}
     * }
     * @param {object|undefined} viewport objekt kojeg prihvaća google maps fitBounds() metoda, ako ga nema koristi se
     * fiksni zoom
     */
    self.focusMapOnLocation = function focusMapOnLocation(location, viewport) {
        if (viewport) {
            map.fitBounds(viewport, 0);
        } else {
            map.panTo({
                lat: location.latitude,
                lng: location.longitude,
            });
            map.setZoom(15);
        }

        return self.updateAndForbidForMiliseconds(500, {
            shouldFitAllResults: false
        }).done(function () {
            createAndAddMarkerFromLocation(location);
        });
    };

    self.searchOnViewport = function searchOnViewport(viewport) {
        map.fitBounds(viewport, 0);

        return self.updateAndForbidForMiliseconds(500, { shouldFitAllResults: false });
    }

    /**
     * @param {function} callback
     */
    self.onReady = function onReady(callback) {
        validateType('function', callback);
        readyCallback = callback;
    };

    self.beforeUpdate = function beforeUpdate(callback) {
        validateType('function', callback);
        beforeUpdateCallbacks.push(callback);
    };

    /**
     * Dopušta registraciju više callbacka, pozivaju se redom kojim su registrirani.
     *
     * @param {function} callback
     */
    self.afterUpdate = function afterUpdate(callback) {
        validateType('function', callback);
        afterUpdateCallbacks.push(callback);
    };

    /**
     * @param {int} miliseconds
     * @param {object} options
     * {
     *     shouldFitAllResults: {bool} repozicionira/odzumira kartu kako bi svi rezultati stali,
     *     useLocationFilter: {bool} false po defaultu,
     * }
     *
     * @return {jqXHR}
     */
    self.updateAndForbidForMiliseconds = function updateAndForbidForMiliseconds(miliseconds, options) {
        options = $.extend({ useLocationFilter: false }, options);
        var resultsPromise = findResultsAsync(options.useLocationFilter);

        forbidResultsRequestForMiliseconds(miliseconds);

        return resultsPromise.done(function setResultsOnMap(results) {
            self.setResults(results, options.shouldFitAllResults);
        });
    };

    /**
     * Fokusira kartu na objekt.
     * @param {number} facilityId
     */
    self.focusOnFacility = function focusOnFacility(facilityId, doubleFacilities, lat, lng) {
        focusMapOnMarkerWithFacilityId(facilityId, doubleFacilities, lat, lng);
    };

    /**
     * Vraća koordinate najsjeveroistočnije i najjugozapadnije točke karte.
     *
     * Ako se karta još nije otvorila, vraća trenutni koordinate viewport-a iz forme. Ako nema koordinata u formi vraća
     * null.
     *
     * @returns {object|null}
     * {
     *     getNorthEast() objekt sa lat() i lng() metodama
     *     getSouthWest() objekt sa lat() i lng() metodama
     * }
     */
    self.getBounds = function getBounds() {
        var newBounds = getMapBounds();

        return mapNotOpenedYet(newBounds) ? options.getInitialViewport() : newBounds;
    };

    /**
     * Resize-a kartu zadržavajući centar.
     *
     * @return {Promise} $.Deferred promise koji kod resolve-a ne šalje nikakve vrijednosti u handler
     */
    self.resize = function resize() {
        var deferred = $.Deferred();
        var center = map.getCenter();
        // Kod inicijalnog otvaranja prozora karte resize event završi *prije* nego se promijene granice karte (bounds).
        // Pošto se radi o inicijalnom otvaranju prozora, točke granice (northeast i southwest) su iste, pa pretraga ne
        // vraća nijedan rezultat (npr. kod prvog otvaranja mape kod pretrage nekog našeg mjesta). Stoga inicijalno
        // slušamo za bounds_changed koji će se u tom slučaju sigurno pokrenuti nakon resize-a, ali neće nakon toga.
        var eventToListenFor = mapNotOpenedYet(getMapBounds()) ? 'bounds_changed' : 'resize';

        google.maps.event.addListenerOnce(map, eventToListenFor, function () {
            map.setCenter(center);
            deferred.resolve();
        });
        google.maps.event.trigger(map, 'resize');

        return deferred.promise();
    };

    function onReady(callback) {
        google.maps.event.addListenerOnce(map, 'idle', callback);
    }

    /**
     * Nije u potpunosti precizno.
     * Koristimo isUserInteractingWithMap jer zoom_changed ne mora inicirati korisnik.
     * Ne koristimo bounds_changed jer bi rezultiralo eventovima koje nije pokrenuo korisnik interakcijom s kartom.
     */
    function onUserInteractionEnd(callback) {
        // workaround oko činjenice da se zoom_changed invoke-a prije same zoom radnje
        map.addListener('zoom_changed', function () {
            google.maps.event.addListenerOnce(map, 'bounds_changed', function () {
                if (isUserInteractingWithMap) {
                    callback();
                }
            });
        });
        map.addListener('dragend', callback);
    }

    function onCenterChanged(callback) {
        map.addListener('center_changed', callback);
    }

    function onZoomIn(callback)  {
        map.addListener('zoom_changed', function () {
            var zoom = map.getZoom();
            var isZoomIn = zoom > previousZoom;

            if (!isZoomIn) {
                return;
            }

            callback(zoom);
            previousZoom = zoom;
        });
    }

    function onZoomOut(callback) {
        map.addListener('zoom_changed', function () {
            var zoom = map.getZoom();
            var isZoomOut = zoom < previousZoom;

            if (!isZoomOut) {
                return;
            }

            callback(zoom);
            previousZoom = zoom;
        });
    }

    function onDragEnd(callback) {
        map.addListener('dragend', callback);
    }

    function onMapContainerClick(callback) {
        elements.mapSearchContainer.addEventListener('click', callback);
    }

    function onControlKeyPressed(callback) {
        $(document)
            .off('keydown.controlKeyPressed')
            .on('keydown.controlKeyPressed', function (e) {
                if (e.keyCode === 17) {
                    callback();
                }
            });
    }

    function onMapContainerMouseout(callback) {
        elements.mapSearchContainer.addEventListener('mouseleave', callback);
    }

    function updateIfAllowedAndForbidForMiliseconds(miliseconds, options) {
        executeCallbacks(beforeUpdateCallbacks);
        var resultsPromise = findResultsAsyncIfAllowed(options.useLocationFilter);

        if (!resultsPromise) {
            return;
        }

        forbidResultsRequestForMiliseconds(miliseconds);
        resultsPromise.done(function (results) {
            self.setResults(results, options.shouldFitAllResults);

            executeCallbacks(afterUpdateCallbacks);
        });
    }

    function executeCallbacks(callbacks) {
        callbacks.forEach(function(callback) { callback(); });
    }

    /**
     * @return {jqXHR}
     */
    function findResultsAsync(useLocationFilter) {
        return resultsProvider.findResultsAsync(getFilters(useLocationFilter));
    }

    /**
     * @return {jqXHR}
     */
    function findResultsAsyncIfAllowed(useLocationFilter) {
        return resultsProvider.findResultsAsyncIfAllowed(getFilters(useLocationFilter));
    }

    function refreshResults(results) {
        clearAllResults();

        if (results) {
            addResults(results);
        }
    }

    function clearAllResults() {
        removeMarkersFromMap(markers);
        markers = [];
    }

    function addResults(results) {
        for (var i = 0; i < results.length; i++) {
            createAndAddMarkerFromResult(results[i]);
        }
    }

    function repositionMapToFitAllResults() {
        if (!areThereAnyResults()) {
            return;
        }

        var bounds = determineMapBoundsByMarkers();
        map.fitBounds(bounds);

        // protiv maximalnog zooma na, npr., 1 rezultat
        if (map.getZoom() > 12) {
            map.setZoom(12);
        }
    }

    function areThereAnyResults() {
        return markers.length > 0;
    }

    function forbidResultsRequestForMiliseconds(miliseconds) {
        resultsProvider.forbidResultsRequest();
        resultsProvider.allowResultsRequestAfter(miliseconds);
    }

    function hightlightFacilityAndShowDetails(facilityId, doubleFacilities) {
        const marker = findMarkerWithFacilityId(facilityId);

        options.onFacilityMarkerFocus({ marker, facilityId, doubleFacilities, bounds: self.getBounds() });
        hightlightMarkerWithFacilityId(facilityId);
    }

    /**
     * @param {int} facilityId
     *
     * @return {Promise} jQuery.deferred() objekt čiji success handler ne prima nijedan argument
     */
    function focusMapOnMarkerWithFacilityId(facilityId, doubleFacilities, latitude, longitude) {
        focusOnPosition({latitude, longitude});

        return self.updateAndForbidForMiliseconds(500, { shouldFitAllResults: false }).then(function () {
            hightlightFacilityAndShowDetails(facilityId, doubleFacilities);
        });
    }

    /**
     * Ističe objekt na karti.
     *
     * @param {number} facilityId
     */
    function hightlightMarkerWithFacilityId(facilityId) {
        var marker = findMarkerWithFacilityId(facilityId);

        if (marker) {
            highlightMarker(marker);
            marker.bounceXTimes(3);
        }
    }

    function determineMapBoundsByMarkers() {
        var bounds = new google.maps.LatLngBounds();

        markers.forEach(function(marker) {
            bounds.extend(marker.getPosition());
        });

        return bounds;
    }

    function createAndAddMarkerFromResult(result) {
        isResultACluster(result) ?
            createAndAddMarkerFromCluster(result) :
            createAndAddMarkerFromFacility(result);
    }

    function isResultACluster(result) {
        return Boolean(result.count);
    }

    function findMarkerWithFacilityId(facilityId) {
        for (var i = 0; i < markers.length; i++) {
            var marker = markers[i];

            if (marker.facilityId === facilityId) {
                return marker;
            }
        }

        return false;
    }

    function highlightMarker(marker) {
        marker.highlight();
    }

    function removeMarkersFromMap(markersToDelete) {
        for (var i = 0; i < markersToDelete.length; i++) {
            markersToDelete[i].setMap(null);
        }
    }

    function createAndAddMarkerFromFacility(facility) {
        markers.push(createMarker(facility, onFacilityMarkerClick));
    }

    function createAndAddMarkerFromCluster(cluster) {
        var marker = new google.maps.Marker({
            map: map,
            icon: urls.facilitiesClusterIcon,
            corners: {
                northeast: cluster.northeasternmostCorner,
                southwest: cluster.southwesternmostCorner
            },
            position: {
                lat: parseFloat(cluster.location.latitude),
                lng: parseFloat(cluster.location.longitude)
            },
            label: {
                color: '#000',
                fontSize: '12px',
                fontWeight: '600',
                text: String(cluster.count)
            }
        });
        marker.removeHighlight = function () {
            this.setIcon(urls.facilitiesClusterIcon);
        };
        marker.highlight = function () {
            this.setIcon(urls.activeFacilitiesClusterIcon);
        };
        marker.addListener('mouseover', function () {
            this.highlight();
        });
        marker.addListener('mouseout', function () {
            this.removeHighlight();
        });
        marker.addListener('click', function () {
            onClusterMarkerClick(this);
        });

        markers.push(marker);
    }

    function createAndAddMarkerFromLocation(location) {
        if (locationMarker) {
            // Uklanjamo stari marker sa karte, ako je prije bio postavljen
            locationMarker.setMap(null);
        }

        locationMarker = new google.maps.Marker({
            map: map,
            icon: urls.placeIcon,
            position: {
                lat: parseFloat(location.latitude),
                lng: parseFloat(location.longitude)
            }
        });
    }

    function onFacilityMarkerClick(marker) {
        options.facilityMarkerClick({
            marker,
            facilityId: marker.facilityId,
            doubleFacilities: marker.doubleFacilities,
            bounds: self.getBounds()
        });

        markers.forEach(function (marker) {
            marker.removeHighlight();
        });
        marker.highlight();

        isUserInteractingWithMap = true;
        analytics('RP-Mapa', 'Objekt', 'klik');
    }

    function onClusterMarkerClick(cluster) {
        isUserInteractingWithMap = true;
        fitMapToClusterContent(cluster);
        analytics('RP-Mapa', 'Cluster', 'klik');
    }

    function fitMapToClusterContent(cluster) {
        var southwest = cluster.corners.southwest;
        var northeast = cluster.corners.northeast;
        var bounds = new google.maps.LatLngBounds(
            new google.maps.LatLng(southwest.latitude, southwest.longitude),
            new google.maps.LatLng(northeast.latitude, northeast.longitude)
        );

        map.fitBounds(bounds);
    }

    function zoomIn(amount) {
        var currentZoom = map.getZoom();
        map.setZoom(currentZoom + amount);
    }

    function zoomOut(amount) {
        var currentZoom = map.getZoom();
        map.setZoom(currentZoom - amount);
    }

    function hasZoomChangedByLessThan(value) {
        var zoom = map.getZoom();

        return Math.abs(zoom - previousZoom) < value;
    }

    function isCenterInsideAllowedBounds() {
        var center = map.getCenter();

        return allowedBounds.contains(center);
    }

    function panToLastValidCenter() {
        if (!lastValidCenter) {
            return;
        }

        map.panTo(lastValidCenter);
    }

    function focusOnPosition(position) {
        map.panTo({
            lat: parseFloat(position.latitude),
            lng: parseFloat(position.longitude)
        });
        map.setZoom(15);
    }

    function getFilters(useLocationFilter) {
        let filters = options.fetchFilterData(self.getBounds());

        filters.push({ name: 'disableClusters', value: map.getZoom() > 11 ? 1 : 0 });

        if (!useLocationFilter) {
            filters.push({ name: 'ignoreLocation', value: 1 });
        }

        return filters;
    }

    function validateType(expected, actual) {
        if (typeof actual !== expected) {
            throw new TypeError('expected a ' + expected + ', got ' + typeof actual);
        }
    }

    function mapNotOpenedYet(bounds) {
        if (bounds === null) {
            return true;
        }

        var boundsLiteral = bounds.getGoogleLiteral();

        return Math.abs(boundsLiteral.north - boundsLiteral.south) < Math.pow(2, -52)
            && Math.abs(boundsLiteral.east - boundsLiteral.west) < Math.pow(2, -52);
    }

    function getMapBounds() {
        // Google maps API getBounds() metoda može vratiti undefined, obično u slučajevima kada korisnik još nije
        // otvorio kartu od učitavanja stranice.
        var bounds = map.getBounds();

        return typeof bounds === 'undefined' ? null : createLatLngBounds({
            north: bounds.getNorthEast().lat(),
            east: bounds.getNorthEast().lng(),
            south: bounds.getSouthWest().lat(),
            west: bounds.getSouthWest().lng(),
        });
    }
}

MapSearch.prototype = {};

export default MapSearch;
